import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parentApi } from '../api';

interface Props {
  mode: 'register' | 'login';
  email?: string;
  onSuccess: (token?: string) => void;
  onError: (message: string) => void;
}

export default function PasskeyButton({ mode, email, onSuccess, onError }: Props) {
  const { i18n } = useTranslation();
  const isFr = i18n.language?.startsWith('fr');
  const [loading, setLoading] = useState(false);

  const isSupported =
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined';

  if (!isSupported) return null;

  const handleClick = async () => {
    setLoading(true);
    try {
      if (mode === 'register') {
        await registerPasskey();
      } else {
        await loginWithPasskey();
      }
    } catch (err) {
      onError(friendlyPasskeyError(err, !!isFr));
    } finally {
      setLoading(false);
    }
  };

  const registerPasskey = async () => {
    const serverOptions = await parentApi.passkeyRegisterBegin();

    const publicKey: PublicKeyCredentialCreationOptions = {
      ...(serverOptions as any),
      challenge: base64urlToBuffer((serverOptions as any).challenge),
      user: {
        ...(serverOptions as any).user,
        id: base64urlToBuffer((serverOptions as any).user.id),
      },
      excludeCredentials: ((serverOptions as any).excludeCredentials ?? []).map((c: any) => ({
        ...c,
        id: base64urlToBuffer(c.id),
      })),
    };

    const credential = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential;
    if (!credential) throw new Error('Passkey creation was cancelled.');

    const attestation = credential.response as AuthenticatorAttestationResponse;
    await parentApi.passkeyRegisterComplete({
      id: credential.id,
      rawId: bufferToBase64url(credential.rawId),
      type: credential.type,
      response: {
        attestationObject: bufferToBase64url(attestation.attestationObject),
        clientDataJSON: bufferToBase64url(attestation.clientDataJSON),
        transports: attestation.getTransports?.() ?? [],
      },
    });

    onSuccess();
  };

  const loginWithPasskey = async () => {
    const { sessionId, options } = await parentApi.passkeyLoginBegin(email);

    const publicKey: PublicKeyCredentialRequestOptions = {
      ...(options as any),
      challenge: base64urlToBuffer((options as any).challenge),
      allowCredentials: ((options as any).allowCredentials ?? []).map((c: any) => ({
        ...c,
        id: base64urlToBuffer(c.id),
      })),
    };

    const assertion = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential;
    if (!assertion) throw new Error('Passkey authentication was cancelled.');

    const assertionRes = assertion.response as AuthenticatorAssertionResponse;
    const result = await parentApi.passkeyLoginComplete(sessionId, {
      id: assertion.id,
      rawId: bufferToBase64url(assertion.rawId),
      type: assertion.type,
      response: {
        clientDataJSON: bufferToBase64url(assertionRes.clientDataJSON),
        authenticatorData: bufferToBase64url(assertionRes.authenticatorData),
        signature: bufferToBase64url(assertionRes.signature),
        userHandle: assertionRes.userHandle ? bufferToBase64url(assertionRes.userHandle) : null,
      },
    });

    onSuccess(result.token);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      style={loading ? { ...s.btn, ...s.loading } : s.btn}
    >
      {loading ? '…' : mode === 'register'
        ? (isFr ? '🔑 Configurer une clé d\'accès' : '🔑 Set up passkey')
        : (isFr ? '🔑 Se connecter avec une clé d\'accès' : '🔑 Sign in with passkey')}
    </button>
  );
}

function friendlyPasskeyError(err: unknown, isFr: boolean): string {
  const msg = err instanceof Error ? err.message : String(err);
  const code = (() => {
    if (/relying party|RP ID|registrable domain/i.test(msg)) return 'PASS-RPID-01';
    if (/not allowed|NotAllowedError/i.test(msg)) return 'PASS-DENIED-02';
    if (/cancelled|canceled/i.test(msg)) return 'PASS-CANCEL-03';
    if (/timed out|timeout/i.test(msg)) return 'PASS-TIMEOUT-04';
    if (/invalid state|InvalidStateError/i.test(msg)) return 'PASS-STATE-05';
    if (/security|SecurityError/i.test(msg)) return 'PASS-SEC-06';
    return 'PASS-ERR-00';
  })();
  const friendly = (() => {
    if (code === 'PASS-RPID-01') return isFr
      ? 'La connexion par clé d\'accès n\'est pas disponible sur ce domaine. Utilisez plutôt votre courriel et votre mot de passe.'
      : 'Passkey sign-in is not available on this domain. Use your email and password instead.';
    if (code === 'PASS-DENIED-02') return isFr
      ? 'L\'accès à la clé d\'accès a été refusé par votre appareil ou votre navigateur.'
      : 'Passkey access was denied by your device or browser.';
    if (code === 'PASS-CANCEL-03') return isFr
      ? 'La demande de clé d\'accès a été annulée. Réessayez lorsque vous serez prêt.'
      : 'Passkey prompt was cancelled. Try again when ready.';
    if (code === 'PASS-TIMEOUT-04') return isFr
      ? 'La demande de clé d\'accès a expiré. Veuillez réessayer.'
      : 'Passkey request timed out. Please try again.';
    if (code === 'PASS-STATE-05') return isFr
      ? 'Une clé d\'accès est déjà enregistrée pour ce compte sur cet appareil.'
      : 'A passkey is already registered for this account on this device.';
    if (code === 'PASS-SEC-06') return isFr
      ? 'Une erreur de sécurité s\'est produite avec la clé d\'accès. Essayez un autre navigateur ou appareil.'
      : 'A security error occurred with the passkey. Try a different browser or device.';
    return isFr
      ? 'La connexion par clé d\'accès a échoué. Utilisez plutôt votre courriel et votre mot de passe.'
      : 'Passkey sign-in failed. Use your email and password instead.';
  })();
  console.error('[PasskeyButton] WebAuthn error:', msg, 'Code:', code);
  return `${friendly} [${code}]`;
}

function base64urlToBuffer(b64: string): ArrayBuffer {
  const padded = b64.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (b64.length % 4)) % 4);
  const binary = atob(padded);
  const buf = new ArrayBuffer(binary.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < binary.length; i++) view[i] = binary.charCodeAt(i);
  return buf;
}

function bufferToBase64url(buf: ArrayBuffer | null | undefined): string {
  if (!buf) return '';
  const bytes = new Uint8Array(buf);
  let binary = '';
  bytes.forEach(b => (binary += String.fromCharCode(b)));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

const s: Record<string, React.CSSProperties> = {
  btn: {
    width: '100%', padding: '12px', borderRadius: '10px',
    border: '1.5px solid #D1D1D6', background: '#fff', cursor: 'pointer',
    fontSize: '0.95rem', fontWeight: 600, color: '#1D1D1F',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
  },
  loading: { opacity: 0.6, cursor: 'not-allowed' },
};
